// Wave 20.13 — Inbox of drops sent to me.
//
// Lightweight card list. Each row shows the sender, preview title/
// subtitle, unread pill, and a big "Open drop" button that goes to
// the drop page. Clicking Open also marks the row as seen so the
// unread badge (elsewhere) ticks down.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface InboxItem {
  id: string;
  dropType: 'activity' | 'peak';
  dropToken: string;
  url: string;
  sender: { id: number; username: string | null; displayName: string };
  seenAt: string | null;
  createdAt: string;
  preview: { title: string; subtitle: string };
  expired: boolean;
}

export default function DropInbox() {
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      if (!jwt) throw new Error('not signed in');
      const res = await fetch(`${API}/drops/inbox`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setItems(await res.json());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markSeen = async (id: string) => {
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      await fetch(`${API}/drops/inbox/${id}/seen`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${jwt}` },
      });
    } catch {}
    setItems((prev) =>
      prev
        ? prev.map((r) => (r.id === id ? { ...r, seenAt: new Date().toISOString() } : r))
        : prev,
    );
  };

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }
  if (items === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.brand }} />
      </Box>
    );
  }
  if (items.length === 0) {
    return (
      <Typography sx={{ color: colors.textMuted, fontStyle: 'italic', py: 2 }}>
        No drops yet — nothing has been sent to you.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {items.map((it) => (
        <Box
          key={it.id}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.surface,
            border: `1px solid ${it.seenAt ? colors.border : colors.brand}`,
            opacity: it.expired ? 0.5 : 1,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 800, color: colors.text }}>
                  {it.sender.displayName}
                </Typography>
                {it.sender.username && (
                  <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                    @{it.sender.username}
                  </Typography>
                )}
                {!it.seenAt && !it.expired && (
                  <Chip
                    label="NEW"
                    size="small"
                    sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 800, height: 18, fontSize: '0.65rem' }}
                  />
                )}
                {it.expired && (
                  <Chip label="EXPIRED" size="small" sx={{ backgroundColor: colors.border, color: colors.textMuted, height: 18, fontSize: '0.65rem' }} />
                )}
              </Stack>
              <Typography sx={{ color: colors.text, fontWeight: 700 }}>
                {it.preview.title}
              </Typography>
              {it.preview.subtitle && (
                <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                  {it.preview.subtitle}
                </Typography>
              )}
              <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem', mt: 0.5 }}>
                {new Date(it.createdAt).toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              disabled={it.expired}
              href={it.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => markSeen(it.id)}
              sx={{ whiteSpace: 'nowrap', color: colors.brand, borderColor: colors.brand }}
            >
              Open
            </Button>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
